import React, { Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import '@sequelae/lore-core/dist/styles/index.css';
import { Spinner, ErrorDisplay } from '@sequelae/lore-core';
import { ThemeProvider } from './contexts/ThemeContext';
import {
  NavigationTestSkeleton,
  AccessibilityDemoSkeleton
} from './components/PageSkeletons';
import {
  useBridgeRouteObserver,
  registerForNavigationDeeplink,
  registerForNavigationGoBack,
  registerForLifecycleEvents
} from '@sequelae/lore-mfe-kit';
import { requestHostCloseSurface } from './utils/hostCompletion';
import { evaluateTrustedHostMessageEvent, isTrustedHostMessageEvent } from './utils/postMessageTrust';

const Settings = React.lazy(() => import('./components/Settings'));
const ProfileDetails = React.lazy(() => import('./components/ProfileDetails'));
const TechnicalSupport = React.lazy(() => import('./components/TechnicalSupport'));
const Activity = React.lazy(() => import('./components/Activity'));
const MfeKitTestHub = React.lazy(() => import('./components/MfeKitTestHub'));
const MfeKitNavigation = React.lazy(() => import('./components/MfeKitNavigation'));
const MfeKitDeepLinks = React.lazy(() => import('./components/MfeKitDeepLinks'));
const MfeKitAPIs = React.lazy(() => import('./components/MfeKitAPIs'));
const MfeKitEdgeCases = React.lazy(() => import('./components/MfeKitEdgeCases'));
const AccessibilityDemo = React.lazy(() => import('./components/AccessibilityDemo'));
const CameraLocationTest = React.lazy(() => import('./components/CameraLocationTest'));

const ALLOWED_HOST_MESSAGE_TYPES = new Set([
  'HOST_BOOTSTRAP',
  'HOST_AUTH_RESULT',
  'AUTH_BOOTSTRAP',
  'AUTH_EVENT',
  'AUTH_TOKEN_RESPONSE',
]);

const BOOTSTRAP_TIMEOUT_MS = 8000;
const AUTH_DEBUG_INFO_ENABLED = process.env.NODE_ENV !== 'production';

type CommonMfeProps = {
  hostAuthResult?: unknown;
  externalRefUserId?: string;
};

function App(props: CommonMfeProps) {
  //useClickEventTracker();

  const [hostRefUserId, setHostRefUserId] = React.useState<string | null>(null);
  const [bootstrapTimedOut, setBootstrapTimedOut] = React.useState(false);
  const firstUserIdSourceLoggedRef = React.useRef(false);

  const hostAuthResultRefUserId = React.useMemo(() => {
    const value = props.hostAuthResult;
    if (!value || typeof value !== 'object') return null;

    const obj = value as Record<string, unknown>;
    const candidates: unknown[] = [
      obj.refUserId,
      obj.ref_user_id,
      obj.externalRefUserId,
      obj.userId,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
      if (typeof candidate === 'number') return String(candidate);
    }
    return null;
  }, [props.hostAuthResult]);

  React.useEffect(() => {
    if (AUTH_DEBUG_INFO_ENABLED && window.parent !== window && (!props.hostAuthResult || typeof props.hostAuthResult !== 'object')) {
      console.info('[DiagnosticsAuthDebug] hostAuthResult prop unavailable at bootstrap', {
        reason: 'runtime_props_missing_host_auth_result',
        embedded: true,
      });
    }
  }, [props.hostAuthResult]);

  React.useEffect(() => {
    const getRefUserIdFromPayload = (payload: unknown): string | null => {
      if (!payload || typeof payload !== 'object') return null;
      const obj = payload as Record<string, unknown>;
      const hostAuthResult = obj.hostAuthResult;
      const authResult = obj.authResult;
      const candidates: unknown[] = [
        obj.refUserId,
        obj.ref_user_id,
        obj.externalRefUserId,
        typeof hostAuthResult === 'object' && hostAuthResult !== null ? (hostAuthResult as Record<string, unknown>).refUserId : null,
        typeof hostAuthResult === 'object' && hostAuthResult !== null ? (hostAuthResult as Record<string, unknown>).ref_user_id : null,
        typeof authResult === 'object' && authResult !== null ? (authResult as Record<string, unknown>).refUserId : null,
        typeof authResult === 'object' && authResult !== null ? (authResult as Record<string, unknown>).ref_user_id : null,
      ];

      for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
        if (typeof candidate === 'number') return String(candidate);
      }

      return null;
    };

    const handler = (event: MessageEvent<unknown>) => {
      const raw = event.data;
      if (!raw || typeof raw !== 'object') return;
      const msg = raw as { type?: string; payload?: unknown };
      const type = msg.type;
      const isAllowedHostType = !!type && ALLOWED_HOST_MESSAGE_TYPES.has(type);

      if (isAllowedHostType) {
        const trustDecision = evaluateTrustedHostMessageEvent(event);
        if (!trustDecision.trusted) {
          console.warn('[DiagnosticsAuthDebug] app host message rejected', {
            type,
            reason: trustDecision.reason,
            origin: event.origin,
            expectedOrigin: trustDecision.expectedOrigin,
            allowedEmbeddedOrigins: trustDecision.allowedEmbeddedOrigins,
          });
          return;
        }
      } else if (!isTrustedHostMessageEvent(event)) {
        return;
      }

      if (!type || !ALLOWED_HOST_MESSAGE_TYPES.has(type)) {
        if (type) {
          console.warn('[DiagnosticsAuthDebug] app host message rejected', {
            type,
            reason: 'missing_expected_type',
            origin: event.origin,
          });
        }
        return;
      }

      const hostUser = getRefUserIdFromPayload(msg.payload);
      if (hostUser) {
        setHostRefUserId(prev => (prev === hostUser ? prev : hostUser));
      } else {
        console.warn('[DiagnosticsAuthDebug] app host message payload parsing failure', {
          type,
          reason: 'payload_parsing_failure',
          origin: event.origin,
        });
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Get ref_user_id from URL parameters
  const urlParams = new URLSearchParams(window.location.search);

  const urlUserId = urlParams.get('ref_user_id');

  const localStorageUserId = window.localStorage.getItem('ref_user_id');

  const userContext = React.useMemo(() => {
    const externalRefUserId =
      typeof props.externalRefUserId === 'string' && props.externalRefUserId.trim()
        ? props.externalRefUserId.trim()
        : null;

    if (hostRefUserId) {
      return { refUserId: hostRefUserId, source: 'host_message' as const };
    }

    if (externalRefUserId) {
      return { refUserId: externalRefUserId, source: 'external_ref_user_id_prop' as const };
    }

    if (hostAuthResultRefUserId) {
      return { refUserId: hostAuthResultRefUserId, source: 'host_auth_result_prop' as const };
    }

    if (urlUserId) {
      return { refUserId: urlUserId, source: 'url_query' as const };
    }

    if (localStorageUserId) {
      return { refUserId: localStorageUserId, source: 'local_storage' as const };
    }

    return { refUserId: '', source: 'none' as const };
  }, [hostRefUserId, props.externalRefUserId, hostAuthResultRefUserId, urlUserId, localStorageUserId]);

  const refUserId = userContext.refUserId;

  React.useEffect(() => {
    if (!AUTH_DEBUG_INFO_ENABLED || firstUserIdSourceLoggedRef.current || !refUserId) {
      return;
    }

    firstUserIdSourceLoggedRef.current = true;
    console.info('[DiagnosticsAuthDebug] first user id source selected', {
      source: userContext.source,
      embedded: window.parent !== window,
    });
  }, [refUserId, userContext.source]);

  // Store in localStorage for future use (only when it changes)
  React.useEffect(() => {
    if (refUserId && refUserId !== localStorageUserId) {
      window.localStorage.setItem('ref_user_id', refUserId);
    }
  }, [refUserId, localStorageUserId]);

  React.useEffect(() => {
    if (refUserId) {
      return;
    }

    if (AUTH_DEBUG_INFO_ENABLED) {
      console.info('[DiagnosticsAuthDebug] bootstrap wait started', {
        timeoutMs: BOOTSTRAP_TIMEOUT_MS,
        embedded: window.parent !== window,
        hasHostAuthResultProp: !!(props.hostAuthResult && typeof props.hostAuthResult === 'object'),
        hostRefUserIdPresent: !!hostRefUserId,
        externalRefUserIdPresent: typeof props.externalRefUserId === 'string' && props.externalRefUserId.trim().length > 0,
      });
    }

    const timeoutId = window.setTimeout(() => {
      console.warn('[DiagnosticsAuthDebug] bootstrap wait timed out before user context', {
        timeoutMs: BOOTSTRAP_TIMEOUT_MS,
        embedded: window.parent !== window,
        hasHostAuthResultProp: !!(props.hostAuthResult && typeof props.hostAuthResult === 'object'),
        hostRefUserIdPresent: !!hostRefUserId,
        externalRefUserIdPresent: typeof props.externalRefUserId === 'string' && props.externalRefUserId.trim().length > 0,
      });
      setBootstrapTimedOut(true);
    }, BOOTSTRAP_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [refUserId, hostRefUserId, props.externalRefUserId, props.hostAuthResult]);

  if (!refUserId) {
    if (bootstrapTimedOut) {
      return (
        <ThemeProvider>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '1rem' }}>
            <ErrorDisplay
              variant="inline"
              title="Unable to initialize session"
              message="Diagnostics did not receive user context from the host in time. Please retry from the host app or refresh this page."
              actions={[{ label: 'Retry', onClick: () => { window.location.reload(); } }]}
            />
          </div>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Spinner size="md" />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <HashRouter>
        <AppContent
          userId={refUserId}
        />
      </HashRouter>
    </ThemeProvider>
  );
}

// Define valid internal routes for Diagnostics MFE (constant, outside component)
const VALID_INTERNAL_ROUTES = [
  '/', // Redirects to /settings (original default)
  '/settings', // Original settings page (default)
  '/settings/profile', // Profile details (Personal Info, Account Status, System Info)
  '/settings/support', // Technical support
  '/activity', // Keep for backward compatibility
  '/diagnostics/activity', // New MFE-prefixed route (recommended for deep links)
  '/mfe-kit-tests',
  '/mfe-kit-navigation',
  '/mfe-kit-deep-links',
  '/mfe-kit-apis',
  '/mfe-kit-edge-cases',
  '/accessibility', // Accessibility demo
  '/camera-location',
] as const;

// Check if a route is external (should be handled by bridge)
const isExternalRoute = (path: string): boolean => {
  const externalRoutePatterns = [
    /^\/rewards\//,
    /^\/community\//,
    /^\/explorations\//,
    /^\/lorebot\//,
    /^\/account\//,
    /^\/streaks/,
    /^\/verification/
  ];
  return externalRoutePatterns.some(pattern => pattern.test(path));
};

// Separate component to use Router hooks
function AppContent({
  userId
}: {
  userId: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // Route observer is provided by lore-mfe-kit and should remain library-owned.
  useBridgeRouteObserver([
    /^\/rewards\//,
    /^\/community\//,
    /^\/explorations\//,
    /^\/lorebot\//,
    /^\/account\//,
    /^\/streaks/,
    /^\/verification/
  ]);

  // Restore last route on mount
  React.useEffect(() => {
    const savedRoute = window.localStorage.getItem('diagnostics_last_route');
    if (savedRoute && savedRoute !== location.pathname) {
      // Validate that the saved route still exists; fall back to /settings for orphaned routes
      const isValidRoute = (VALID_INTERNAL_ROUTES as readonly string[]).includes(savedRoute);
      const targetRoute = isValidRoute ? savedRoute : '/settings';
      void navigate(targetRoute, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Save current route whenever it changes
  React.useEffect(() => {
    window.localStorage.setItem('diagnostics_last_route', location.pathname);
  }, [location.pathname]);

  // Handle lifecycle events using mfe-kit hook
  registerForLifecycleEvents({
    onViewWillDisappear: () => {
      window.localStorage.setItem('diagnostics_last_route', location.pathname);
    },
    onViewDidAppear: () => {
      const savedRoute = window.localStorage.getItem('diagnostics_last_route');
      if (savedRoute && savedRoute !== location.pathname) {
        // Validate that the saved route still exists; fall back to /settings for orphaned routes
        const isValidRoute = (VALID_INTERNAL_ROUTES as readonly string[]).includes(savedRoute);
        const targetRoute = isValidRoute ? savedRoute : '/settings';
        void navigate(targetRoute, { replace: true });
      }
    },
  });

  // Handle deep link navigation using mfe-kit hook
  registerForNavigationDeeplink({
    onDeeplink: (path) => {
      // Normalize path (ensure it starts with /)
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;

      // Check if it's an external route
      if (isExternalRoute(normalizedPath)) {
        return;
      }

      // Check if it's a valid internal route
      const isValidStaticRoute = (VALID_INTERNAL_ROUTES as readonly string[]).includes(normalizedPath);
      if (!isValidStaticRoute) {
        console.warn('⚠️ Invalid internal route for deep link:', normalizedPath);
        return;
      }

      // Check if we're already on this route
      if (location.pathname === normalizedPath) {
        return;
      }

      // Navigate to the route
      void navigate(normalizedPath);
    }
  });

  // Handle go back navigation using mfe-kit hook.
  // Pages render no in-MFE back control; the host back button is the only way out.
  registerForNavigationGoBack({
    onGoBack: () => {
      // Get current pathname
      const currentPath = location.pathname;

      // Check if we're at root routes where back should close MFE
      const isAtRoot = currentPath === '/settings' || currentPath === '/';

      // React Router stores its history index in history.state.idx (0 on first load,
      // unchanged by replace). With no earlier in-MFE entry, e.g. an initialPath deep
      // link, navigate(-1) would leave the WebView, so close the surface instead.
      const historyIndex = (window.history.state as { idx?: number } | null)?.idx;
      const hasInAppHistory = typeof historyIndex === 'number' && historyIndex > 0;

      if (isAtRoot || !hasInAppHistory) {
        void requestHostCloseSurface('diagnostics-root-go-back');
        return true;
      }

      // Navigate back in history
      void navigate(-1);
      return true; // We handled it internally
    },
    autoFinishWhenAtRoot: false
  });

  // Check if initialPath is present in URL (from iOS MFEType.custom)
  const initialPath = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const path = params.get('initialPath');
    // Validate that initialPath is in VALID_INTERNAL_ROUTES; fall back to /settings for orphaned routes
    if (path && (VALID_INTERNAL_ROUTES as readonly string[]).includes(path)) {
      return path;
    }
    return null;
  }, []);

  return (
    <div className="App">
      <div className="content">
        <Suspense fallback={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <Spinner size="md" />
          </div>
        }>
          <Routes>
            <Route
              path="/"
              element={initialPath ? <Navigate to={initialPath} replace /> : <Navigate to="/settings" replace />}
            />
            <Route path="/settings" element={<Settings userId={userId} />} />
            <Route path="/settings/profile" element={<ProfileDetails userId={userId} />} />
            <Route path="/settings/support" element={<TechnicalSupport userId={userId} />} />
            <Route path="/activity" element={<Activity userId={userId} />} />
            <Route path="/diagnostics/activity" element={<Activity userId={userId} />} />
            <Route path="/mfe-kit-tests" element={
              <Suspense fallback={<NavigationTestSkeleton />}>
                <MfeKitTestHub />
              </Suspense>
            } />
            <Route path="/mfe-kit-navigation" element={<MfeKitNavigation userId={userId} />} />
            <Route path="/mfe-kit-deep-links" element={<MfeKitDeepLinks />} />
            <Route path="/mfe-kit-apis" element={<MfeKitAPIs />} />
            <Route path="/mfe-kit-edge-cases" element={<MfeKitEdgeCases />} />
            <Route path="/accessibility" element={
              <Suspense fallback={<AccessibilityDemoSkeleton />}>
                <AccessibilityDemo userId={userId} />
              </Suspense>
            } />
            <Route path="/camera-location" element={<CameraLocationTest />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

export default App;
